import { useState, useMemo, useRef, useEffect } from 'react';
import { Subscription } from 'rxjs';
import LiveblogWidgets, {
    IWidget,
    ILiveblogWidgetsConfig,
    IWidgetSubscription,
    IPostMapped,
    IPinnedPostMapped,
    SubscriptionsNames,
} from '@sky-it-cmscomposer/liveblog-widgets';
import { usePageTitle } from './usePageTitle';
import { getServiceConfiguration } from '../../utils/service-config';
import { isEmptyObject, isValidObject } from '../../utils/props-validator';
import { postIntoListComplexityHandler, removePost } from '../../utils/subscriptions-utils';
import { setDocumentTitle } from '../../utils/page-utils';
import { IGenericObject } from '../../typings/common-types';

export interface IUseService {
    live?: boolean;
    initialValue?: IGenericObject;
    query: IGenericObject;
    widget: IWidget;
    setNewPostsBadgeCounter: (value: any) => void;
    setNewPostsLabelCounter: (value: any) => void;
}

const serviceConfig = (): ILiveblogWidgetsConfig => {
    try {
        const { apikey: apiKey, endpoint, realtimeEndpoint, realtimeHost } = getServiceConfiguration();
        return {
            apiKey,
            endpoint,
            realtime: true,
            realtimeEndpoint,
            realtimeHost,
        };
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in Service configuration');
        return {
            apiKey: 'fallback-apikey',
            endpoint: 'fallback-endpoint',
        };
    }
};

const service = new LiveblogWidgets(serviceConfig());

export const useService = ({
    /* istanbul ignore next: default value */ live = false,
    /* istanbul ignore next: default value */ initialValue = {},
    /* istanbul ignore next: default value */ query = {},
    widget,
    setNewPostsBadgeCounter,
    setNewPostsLabelCounter,
}: IUseService) => {
    const widgetRef = useRef(widget);
    const widgetCurrent: IWidget = widgetRef.current;
    const subscriptionsRefs = widgetCurrent.subscriptions?.map(() => useRef(new Subscription())) || [];

    const [queryState] = useState<IGenericObject>(query);
    const [model, setModel] = useState<IGenericObject>(initialValue);
    const [error, setError] = useState<number>(0);
    const { pageTitle } = usePageTitle();

    // Subscriptions callbacks config
    const subscriptionsCallbacks: { [key in SubscriptionsNames]: any } = useMemo(
        () => ({
            onPublishPost: (post: IPostMapped | IPinnedPostMapped) => {
                setModel((prevModel: IGenericObject) => {
                    const allPostIds = [...(prevModel.posts || []), ...(prevModel.pinnedPosts || [])].map((post) => post.postId);
                    const isNewPost = !allPostIds.includes(post.postId);
                    if (isNewPost) {
                        setNewPostsBadgeCounter((prevBadgeCounter: number) => {
                            setDocumentTitle(pageTitle, prevBadgeCounter + 1);
                            return prevBadgeCounter + 1;
                        });
                        setNewPostsLabelCounter((prevLabelCounter: number) => {
                            return prevLabelCounter + 1;
                        });
                    }
                    return {
                        ...prevModel,
                        ...postIntoListComplexityHandler({ posts: prevModel.posts || [], pinnedPosts: prevModel.pinnedPosts || [], post }),
                    };
                });
            },
            onDeletePost: (post: IPostMapped | IPinnedPostMapped) => {
                setModel((prevModel: IGenericObject) => ({
                    ...prevModel,
                    posts: removePost(prevModel.posts || [], post),
                    pinnedPosts: removePost(prevModel.pinnedPosts || [], post),
                }));
            },
        }),
        []
    );

    /* Query Hook */
    useEffect(() => {
        if (!model || isEmptyObject(model)) {
            const doQuery = async () => {
                try {
                    const newData: IGenericObject = await service.query(widgetCurrent.query, queryState);
                    if (isValidObject(newData)) {
                        setModel(newData);
                    }
                } catch (errorData) {
                    setError(500);
                }
            };
            doQuery();
        }
    }, []);

    /* Subscriptions Hooks   */
    widgetCurrent.subscriptions?.forEach(({ name, widgetSubscription }: IWidgetSubscription, index: number) => {
        useEffect(() => {
            try {
                if (live) {
                    // @ts-ignore
                    subscriptionsRefs[index].current = service.subscription(widgetSubscription, queryState).subscribe({
                        next: (newPost: IPostMapped | IPinnedPostMapped) => {
                            if (isValidObject(newPost)) {
                                subscriptionsCallbacks[name](newPost);
                            }
                        },
                        error: (errorCode: unknown) => {
                            // eslint-disable-next-line no-console
                            console.error(errorCode);
                        },
                    });
                }
                return () => subscriptionsRefs[index].current.unsubscribe();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
                return () => subscriptionsRefs[index].current.unsubscribe();
            }
        }, []);
    });

    return { model, error };
};
